import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { useCart } from "../lib/cart";
import { trackEvent } from "../lib/analytics";

/**
 * One-shot toast that fires when we restored a cart from the server.
 *
 * This is the moment that wins back the visitor — they closed the tab
 * yesterday, came back today, and find their work-in-progress design
 * waiting for them. We surface it with a clear "we remembered" message
 * and a direct link to the cart.
 *
 * Mount once at the app root (inside <CartProvider>). The toast fires
 * exactly once per restoration event — `acknowledgeRestore()` flips
 * the flag off after we surface it, so navigating around the app
 * doesn't re-trigger it.
 */
export default function CartRestoredToast() {
  const { items, wasRestored, acknowledgeRestore } = useCart();
  const firedRef = useRef(false);

  useEffect(() => {
    if (!wasRestored || firedRef.current) return;
    if (!items?.length) return;
    firedRef.current = true;
    const total = items.reduce(
      (acc, it) => acc + Number(it.unit_price || 0) * Number(it.quantity || 1),
      0,
    );
    trackEvent("cart_restored_toast_shown", {
      item_count: items.length,
      value: total,
    });
    toast.success(
      <div
        className="flex items-center gap-3"
        data-testid="cart-restored-toast"
      >
        <div className="w-9 h-9 bg-[var(--tc-red,#e02424)] flex items-center justify-center text-white shrink-0">
          <ShoppingBag size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-ui font-bold text-sm uppercase tracking-wide">
            Welcome back — we saved your cart
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            {items.length} item{items.length !== 1 ? "s" : ""} · ${total.toFixed(2)}{" "}
            <Link
              to="/cart"
              className="underline font-bold"
              data-testid="cart-restored-toast-link"
              onClick={() => trackEvent("cart_restored_toast_click", { item_count: items.length })}
            >
              Review →
            </Link>
          </div>
        </div>
      </div>,
      {
        duration: 8000,
        // Acknowledge AFTER the toast is dismissed (auto or manual) so
        // the flag clears and the toast doesn't fire again on remount.
        onDismiss: acknowledgeRestore,
        onAutoClose: acknowledgeRestore,
      },
    );
  }, [wasRestored, items, acknowledgeRestore]);

  return null;
}
